/**
 * Thin typed wrapper over fetch for the Fulfil API.
 *
 * Handles the bearer token, one coordinated refresh when the access token has
 * expired, and turning error responses into a useful Error.
 */
import { getSession, setSession, updateTokens } from './session'
import type { AccessTokenResponse } from './types'

/**
 * Empty in dev, where Vite proxies /api to the backend. In production this is
 * baked in at build time from VITE_API_BASE_URL.
 */
const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')

/** Dev requests go to /api so the dev-server proxy picks them up. */
const PREFIX = BASE || '/api'

/**
 * An uploaded image's URL, ready for <img src>. Production storage returns
 * absolute Firebase URLs; local dev storage returns /static/uploads/… paths,
 * which live on the API.
 */
export function assetUrl(url: string): string {
  return /^https?:\/\//.test(url) ? url : `${PREFIX}${url}`
}

export class ApiError extends Error {
  readonly status: number
  readonly detail: string

  constructor(status: number, detail: string) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }

  /** The caller is signed in but this account may not see the resource. */
  get isForbidden(): boolean {
    return this.status === 403
  }
}

/** Called when refreshing fails, so the UI can send the user back to login. */
let onSignedOut: (() => void) | null = null
export function setSignedOutHandler(handler: () => void): void {
  onSignedOut = handler
}

function signOut(): void {
  setSession(null)
  onSignedOut?.()
}

async function errorFrom(response: Response): Promise<ApiError> {
  let detail = `${response.status} ${response.statusText}`
  try {
    const body = (await response.json()) as { detail?: unknown }
    if (typeof body.detail === 'string') {
      detail = body.detail
    } else if (Array.isArray(body.detail)) {
      // FastAPI validation errors arrive as a list of objects.
      const parts = body.detail
        .map((entry) => {
          const e = entry as { loc?: unknown[]; msg?: string }
          const where = Array.isArray(e.loc) ? e.loc.slice(1).join('.') : ''
          return where ? `${where}: ${e.msg ?? ''}` : (e.msg ?? '')
        })
        .filter(Boolean)
      if (parts.length) detail = parts.join('; ')
    }
  } catch {
    // Not JSON — the status line is the best we have.
  }
  return new ApiError(response.status, detail)
}

// A single in-flight refresh shared by every request that got a 401 at once,
// so a page with six panels does not fire six refreshes and invalidate its own
// rotated token five times over.
let refreshing: Promise<boolean> | null = null

function refreshOnce(): Promise<boolean> {
  if (refreshing) return refreshing

  refreshing = (async () => {
    const session = getSession()
    if (!session) return false
    try {
      const response = await fetch(`${PREFIX}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: session.refreshToken }),
      })
      if (!response.ok) return false
      const tokens = (await response.json()) as AccessTokenResponse
      updateTokens(tokens.access_token, tokens.refresh_token)
      return true
    } catch {
      return false
    } finally {
      // Clear on the next tick so callers awaiting this promise all see the
      // same result before a fresh attempt can start.
      setTimeout(() => {
        refreshing = null
      }, 0)
    }
  })()

  return refreshing
}

export type Query = Record<string, string | number | boolean | null | undefined>

export function queryString(params: Query): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue
    search.set(key, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Skip the bearer token — only login and refresh need this. */
  anonymous?: boolean
  signal?: AbortSignal
}

async function send(path: string, options: RequestOptions, retry: boolean): Promise<Response> {
  const headers: Record<string, string> = {}
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  if (!options.anonymous) {
    const session = getSession()
    if (session) headers.Authorization = `Bearer ${session.accessToken}`
  }

  const response = await fetch(`${PREFIX}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  // 401 means the access token is stale or bad. Refresh once, then retry. A
  // second 401 means the refresh token is finished too, so the session is over.
  if (response.status === 401 && retry && !options.anonymous) {
    if (await refreshOnce()) return send(path, options, false)
    signOut()
  }

  return response
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options, true)
  if (!response.ok) throw await errorFrom(response)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, params?: Query, signal?: AbortSignal) =>
    request<T>(`${path}${params ? queryString(params) : ''}`, { signal }),

  post: <T>(path: string, body?: unknown, options?: { anonymous?: boolean }) =>
    request<T>(path, { method: 'POST', body, anonymous: options?.anonymous }),

  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
}
